// Helpers for seeding the modal's local translation map and merging it into survey updates.

import { QUESTION_TYPES, QUESTION_TYPES_WITH_SUBQUESTIONS } from '@api/instant_survey';
import { stripHtml } from '@utilities';

// Only grid questions expose subQuestions as translatable row labels.
const hasTranslatableSubQuestions = (question) =>
  QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type);

const isMaxDiffQuestion = (question) => question.type === QUESTION_TYPES.MAX_DIFF.value;

export const getEntityKeyFromTranslationKey = (key) => key.split(':')[0];

// Treat whitespace-only rich text, including non-breaking spaces, as empty.
export const hasTranslationText = (richText) =>
  Boolean(
    stripHtml(richText || '')
      .replace(/\u00a0/g, ' ')
      .trim()
  );

const getTranslationLanguageCode = (entry) => entry?.languageCode;
const getTranslationRichText = (entry) => entry?.richText ?? entry?.text;

const addExistingTranslations = (entityKey, entries, target) =>
  (entries || []).forEach((entry) => {
    const code = getTranslationLanguageCode(entry);
    const richText = getTranslationRichText(entry);
    if (code) {
      target[`${entityKey}:${code}`] = {
        isRelevant: entry?.isRelevant ?? true,
        isUserUpdated: entry?.isUserUpdated ?? false,
        richText: richText || '',
      };
    }
  });

export const getInitialTranslations = ({ groups = [], questions = [] }) => {
  const initial = {};
  groups.forEach((group) =>
    addExistingTranslations(`concept-${group.id}`, group.translations, initial)
  );
  questions.forEach((question) => {
    addExistingTranslations(`question-${question.id}`, question.translations, initial);
    if (isMaxDiffQuestion(question)) {
      addExistingTranslations(
        `maxdiff-best-label-${question.id}`,
        question.properties?.bestLabelTranslations,
        initial
      );
      addExistingTranslations(
        `maxdiff-worst-label-${question.id}`,
        question.properties?.worstLabelTranslations,
        initial
      );
    }
    (question.answers || []).forEach((answer) =>
      addExistingTranslations(`option-${question.id}-${answer.id}`, answer.translations, initial)
    );
    if (hasTranslatableSubQuestions(question)) {
      (question.subQuestions || []).forEach((subQuestion) =>
        addExistingTranslations(
          `subquestion-${question.id}-${subQuestion.id}`,
          subQuestion.translations,
          initial
        )
      );
    }
  });
  return initial;
};

export const getSourceTextByEntityKey = ({ groups = [], questions = [] }) => {
  const sourceTextByKey = {};
  groups.forEach((group) => {
    sourceTextByKey[`concept-${group.id}`] = group.conceptName || '';
  });
  questions.forEach((question) => {
    sourceTextByKey[`question-${question.id}`] = question.richText || question.text || '';
    if (isMaxDiffQuestion(question)) {
      sourceTextByKey[`maxdiff-best-label-${question.id}`] = question.properties?.bestLabel || '';
      sourceTextByKey[`maxdiff-worst-label-${question.id}`] = question.properties?.worstLabel || '';
    }
    (question.answers || []).forEach((answer) => {
      sourceTextByKey[`option-${question.id}-${answer.id}`] = answer.richText || answer.text || '';
    });
    if (hasTranslatableSubQuestions(question)) {
      (question.subQuestions || []).forEach((subQuestion) => {
        sourceTextByKey[`subquestion-${question.id}-${subQuestion.id}`] =
          subQuestion.richText || subQuestion.text || '';
      });
    }
  });
  return sourceTextByKey;
};

// Restore draft edits, but keep stale warnings when their source text changed.
export const seedTranslations = (survey, draft) => {
  const base = getInitialTranslations(survey);
  if (!draft) return base;

  const sourceTextByKey = getSourceTextByEntityKey(survey);
  const merged = { ...base };
  Object.entries(draft).forEach(([key, entry]) => {
    if (!entry) return;
    const baseEntry = base[key];
    const entityKey = getEntityKeyFromTranslationKey(key);
    const currentSourceText = sourceTextByKey[entityKey] || '';
    const didDraftSourceChange = entry.sourceText != null && entry.sourceText !== currentSourceText;
    merged[key] = {
      ...entry,
      isRelevant:
        entry.isRelevant &&
        !didDraftSourceChange &&
        (baseEntry ? baseEntry.isRelevant !== false : true),
      sourceText: currentSourceText,
    };
  });
  return merged;
};

// Upsert local translations and mark saved rows missing from the local map with `_delete: true`.
export const buildTranslations = (existing, entityKey, translationsMap, appliedLanguages) => {
  const byLanguage = new Map(
    (existing || []).map((entry) => [getTranslationLanguageCode(entry), entry])
  );

  appliedLanguages.forEach((language) => {
    const entry = translationsMap[`${entityKey}:${language}`];
    const richText = (entry?.richText || '').trim();
    if (!hasTranslationText(richText)) return;
    byLanguage.set(language, {
      ...byLanguage.get(language),
      isRelevant: entry.isRelevant !== false,
      // The user-edited flag is session-only and is not saved on the survey.
      isUserUpdated: false,
      languageCode: language,
      richText,
      text: stripHtml(richText),
    });
  });

  byLanguage.forEach((entry, language) => {
    if (`${entityKey}:${language}` in translationsMap) return;
    if (typeof entry.id === 'number') byLanguage.set(language, { ...entry, _delete: true });
    else byLanguage.delete(language);
  });

  return Array.from(byLanguage.values());
};

// Build the partial survey update, preserving rows marked `_delete` so deletion reaches the backend.
export const buildSurveyUpdates = (
  translationsMap,
  { appliedLanguages = [], concepts = [], questions = [] } = {}
) => {
  const updatedQuestions = questions.map((question) => {
    const answers = (question.answers || []).map((answer) => {
      const answerTranslations = buildTranslations(
        answer.translations,
        `option-${question.id}-${answer.id}`,
        translationsMap,
        appliedLanguages
      );
      return answerTranslations.length ? { ...answer, translations: answerTranslations } : answer;
    });
    const questionTranslations = buildTranslations(
      question.translations,
      `question-${question.id}`,
      translationsMap,
      appliedLanguages
    );
    const bestLabelTranslations = isMaxDiffQuestion(question)
      ? buildTranslations(
          question.properties?.bestLabelTranslations,
          `maxdiff-best-label-${question.id}`,
          translationsMap,
          appliedLanguages
        )
      : [];
    const worstLabelTranslations = isMaxDiffQuestion(question)
      ? buildTranslations(
          question.properties?.worstLabelTranslations,
          `maxdiff-worst-label-${question.id}`,
          translationsMap,
          appliedLanguages
        )
      : [];
    return {
      ...question,
      answers,
      ...(isMaxDiffQuestion(question) &&
      (bestLabelTranslations.length || worstLabelTranslations.length)
        ? {
            properties: {
              ...question.properties,
              ...(bestLabelTranslations.length
                ? { bestLabelTranslations: bestLabelTranslations }
                : {}),
              ...(worstLabelTranslations.length
                ? { worstLabelTranslations: worstLabelTranslations }
                : {}),
            },
          }
        : {}),
      // Only grid subQuestions are translated as row labels.
      ...(hasTranslatableSubQuestions(question)
        ? {
            subQuestions: question.subQuestions.map((subQuestion) => {
              const subQuestionTranslations = buildTranslations(
                subQuestion.translations,
                `subquestion-${question.id}-${subQuestion.id}`,
                translationsMap,
                appliedLanguages
              );
              return subQuestionTranslations.length
                ? { ...subQuestion, translations: subQuestionTranslations }
                : subQuestion;
            }),
          }
        : {}),
      ...(questionTranslations.length ? { translations: questionTranslations } : {}),
    };
  });

  const updatedGroups = concepts.map((group) => {
    // Never send empty concept translations.
    const groupTranslations = buildTranslations(
      group.translations,
      `concept-${group.id}`,
      translationsMap,
      appliedLanguages
    ).filter((entry) => entry._delete || entry.text || entry.richText);
    if (!groupTranslations.length) {
      const { translations: emptyTranslations, ...groupWithoutTranslations } = group;
      void emptyTranslations;
      return groupWithoutTranslations;
    }
    return { ...group, translations: groupTranslations };
  });

  return { groups: updatedGroups, questions: updatedQuestions };
};
